/**
 * Estate Locker Hooks
 *
 * React Query hooks for estate settings, overview, final wishes,
 * document links, and digital assets.
 *
 * @module hooks/useEstateLocker
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuth } from '@xanectech/auth';
import {
  EstateLockerService,
  type FinalWishesInsert,
  type DocumentLinkInsert,
} from '../services/EstateLockerService';
import type { Database } from '@xanectech/types';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('useEstateLocker');

type EstateSettingsInsert =
  Database['public']['Tables']['estate_vault_settings']['Insert'];

// ============================================
// Query keys
// ============================================

export const estateLockerKeys = {
  overview: (userId: string) => ['estate', 'overview', userId] as const,
  settings: (userId: string) => ['estate', 'settings', userId] as const,
  wishes: (userId: string) => ['estate', 'wishes', userId] as const,
  documents: (userId: string) => ['estate', 'documents', userId] as const,
  userDocuments: (userId: string) =>
    ['estate', 'user-documents', userId] as const,
  scopeCategories: (userId: string) =>
    ['estate', 'scope-categories', userId] as const,
  assets: (userId: string) => ['estate', 'assets', userId] as const,
};

// ============================================
// Hooks
// ============================================

/**
 * Fetch estate overview data for the authenticated user.
 * Includes settings, contact counts, completion percentage, and wishes status.
 */
export function useEstateOverview() {
  const { user } = useAuth();
  const userId = user?.id ?? '';

  return useQuery({
    queryKey: estateLockerKeys.overview(userId),
    queryFn: () => EstateLockerService.getEstateOverview(userId),
    enabled: !!userId,
    staleTime: 60_000, // 1 min
  });
}

/**
 * Fetch estate settings for the authenticated user.
 */
export function useEstateSettings() {
  const { user } = useAuth();
  const userId = user?.id ?? '';

  return useQuery({
    queryKey: estateLockerKeys.settings(userId),
    queryFn: () => EstateLockerService.getEstateSettings(userId),
    enabled: !!userId,
    staleTime: 120_000,
  });
}

/**
 * Upsert estate settings mutation.
 */
export function useUpsertEstateSettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: (payload: Omit<EstateSettingsInsert, 'user_id'>) =>
      EstateLockerService.upsertEstateSettings(userId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: estateLockerKeys.settings(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: estateLockerKeys.overview(userId),
      });
    },
    onError: (error) => {
      logger.error('Failed to save estate settings', {
        context: 'useUpsertEstateSettings',
        error: String(error),
      });
    },
  });
}

/**
 * Fetch final wishes for the authenticated user.
 */
export function useFinalWishes() {
  const { user } = useAuth();
  const userId = user?.id ?? '';

  return useQuery({
    queryKey: estateLockerKeys.wishes(userId),
    queryFn: () => EstateLockerService.getFinalWishes(userId),
    enabled: !!userId,
    staleTime: 120_000,
  });
}

/**
 * Upsert final wishes mutation.
 */
export function useUpsertFinalWishes() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: (payload: Omit<FinalWishesInsert, 'user_id'>) =>
      EstateLockerService.upsertFinalWishes(userId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: estateLockerKeys.wishes(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: estateLockerKeys.overview(userId),
      });
    },
    onError: (error) => {
      logger.error('Failed to save final wishes', {
        context: 'useUpsertFinalWishes',
        error: String(error),
      });
    },
  });
}

/**
 * Fetch estate digital assets for the authenticated user.
 */
export function useEstateDigitalAssets() {
  const { user } = useAuth();
  const userId = user?.id ?? '';

  return useQuery({
    queryKey: estateLockerKeys.assets(userId),
    queryFn: () => EstateLockerService.listDigitalAssets(userId),
    enabled: !!userId,
  });
}

/**
 * Fetch the list of document links for the current user's estate.
 */
export function useEstateDocumentLinks() {
  const { user } = useAuth();
  const userId = user?.id ?? '';

  return useQuery({
    queryKey: estateLockerKeys.documents(userId),
    queryFn: () => EstateLockerService.listEstateDocuments(userId),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

/**
 * Fetch all user documents available to be linked into the estate.
 * Excludes archived and deleted documents.
 */
export function useUserDocumentsForEstate() {
  const { user } = useAuth();
  const userId = user?.id ?? '';

  return useQuery({
    queryKey: estateLockerKeys.userDocuments(userId),
    queryFn: () => EstateLockerService.listUserDocuments(userId),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

/**
 * Mutation to link an existing document into the estate vault.
 * Creates a link record only — source document is never modified.
 */
export function useLinkEstateDocument() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: ({
      documentId,
      metadata,
    }: {
      documentId: string;
      metadata?: Pick<
        DocumentLinkInsert,
        'document_category' | 'access_priority' | 'is_critical' | 'notes'
      >;
    }) => EstateLockerService.linkEstateDocument(userId, documentId, metadata),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: estateLockerKeys.documents(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: estateLockerKeys.overview(userId),
      });
    },
    onError: (error) => {
      logger.error('Failed to link document to estate', {
        context: 'useLinkEstateDocument',
        error: String(error),
      });
    },
  });
}

/**
 * Mutation to unlink a document from the estate vault.
 * Deletes only the link record — the source document is never deleted.
 */
export function useUnlinkEstateDocument() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: (linkId: string) =>
      EstateLockerService.unlinkEstateDocument(userId, linkId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: estateLockerKeys.documents(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: estateLockerKeys.overview(userId),
      });
    },
    onError: (error) => {
      logger.error('Failed to unlink document from estate', {
        context: 'useUnlinkEstateDocument',
        error: String(error),
      });
    },
  });
}

/**
 * Fetch personal data categories with entry counts for estate scope display.
 * Categories with entries are available for inclusion in estate release scope.
 */
export function useEstateScopeCategories() {
  const { user } = useAuth();
  const userId = user?.id ?? '';

  return useQuery({
    queryKey: estateLockerKeys.scopeCategories(userId),
    queryFn: () => EstateLockerService.getCategoriesForEstateScope(userId),
    enabled: !!userId,
    staleTime: 60_000,
  });
}
